import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ASSISTANT_RAG_LABELS_FR, RagAskLabels } from '../config/rag-ask.config';
import { ChatMessage } from '../models/ask.models';
import { RagPreviewService } from '../services/rag-preview.service';
import { renderMarkdown } from '../util/markdown';
import { RagAskSourcesComponent } from './rag-ask-sources.component';
import { RagIconComponent } from './rag-icon.component';

export interface DownloadRequest {
  readonly url: string;
  readonly filename: string;
}

@Component({
  selector: 'rag-ask-message',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, RagIconComponent, RagAskSourcesComponent],
  template: `
    <div
      class="rag-msg"
      [class.rag-msg--user]="message.role === 'user'"
      [class.rag-msg--assistant]="message.role !== 'user'"
      [class.rag-msg--error]="message.status === 'error'"
    >
      <div class="rag-msg__wrapper">
        <div *ngIf="message.status === 'error'" class="rag-msg__bubble rag-msg__bubble--error">
          <rag-icon name="warning" [size]="16"></rag-icon>
          <span>{{ message.content || labels.genericError }}</span>
        </div>
        <div
          *ngIf="message.status !== 'error'"
          class="rag-msg__bubble"
          (mouseover)="onPointerOver($event)"
          (focusin)="onPointerOver($event)"
          (mouseout)="onPointerOut($event)"
          (focusout)="onPointerOut($event)"
          (click)="onClick($event)"
        >
          <div class="rag-md" [innerHTML]="html"></div>
          <span *ngIf="message.status === 'streaming'" class="rag-msg__cursor" aria-hidden="true"></span>
        </div>

        <rag-ask-sources
          *ngIf="showSources && message.role !== 'user' && message.status !== 'streaming' && sources.length"
          [sources]="sources"
          [labels]="labels"
          [showScores]="showScores"
          [showFreshness]="showFreshness"
          [locale]="locale"
        ></rag-ask-sources>

        <div class="rag-msg__footer">
          <time class="rag-msg__time" [attr.datetime]="isoTime">{{ time }}</time>
          <button
            *ngIf="message.role !== 'user' && message.status === 'done' && message.content"
            type="button"
            class="rag-msg__copy"
            [attr.aria-label]="copied() ? 'Copié' : 'Copier la réponse'"
            (click)="copy()"
          >
            <rag-icon [name]="copied() ? 'copied' : 'copy'" [size]="14"></rag-icon>
          </button>
        </div>
      </div>
    </div>
  `,
})
export class RagAskMessageComponent {
  private readonly preview = inject(RagPreviewService, { optional: true });
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) message!: ChatMessage;
  @Input() labels: RagAskLabels = ASSISTANT_RAG_LABELS_FR;
  @Input() showSources = true;
  @Input() showScores = false;
  @Input() showFreshness = true;
  @Input() locale: string | undefined;
  @Input() downloadMarker: string | false = '/download/';

  @Output() readonly download = new EventEmitter<DownloadRequest>();

  protected readonly copied = signal(false);

  /**
   * Last rendered answer. `bypassSecurityTrustHtml` returns a new object on
   * every call, so without this cache each change detection (a hover inside
   * the bubble, for instance) would reset `innerHTML` and replace the citation
   * marker the popover is anchored to.
   */
  private htmlCache: {
    readonly content: string;
    readonly streaming: boolean;
    readonly sourceCount: number;
    readonly strip: boolean;
    readonly label: string;
    readonly value: SafeHtml;
  } | null = null;

  protected get sources() {
    return this.message?.sources ?? [];
  }

  protected get html(): SafeHtml {
    const msg = this.message;
    if (!msg) return '';
    const streaming = msg.status === 'streaming';
    const sourceCount = streaming ? 0 : this.showSources ? this.sources.length : 0;
    const strip = !streaming && msg.role !== 'user';
    const label = this.labels.sourcePreview;

    const cached = this.htmlCache;
    if (
      cached &&
      cached.content === msg.content &&
      cached.streaming === streaming &&
      cached.sourceCount === sourceCount &&
      cached.strip === strip &&
      cached.label === label
    ) {
      return cached.value;
    }

    const markup = renderMarkdown(msg.content, {
      sourceCount,
      stripSourcesSection: strip,
      citationLabel: label,
      streaming,
    });
    const value: SafeHtml = markup ? this.sanitizer.bypassSecurityTrustHtml(markup) : '';
    this.htmlCache = { content: msg.content, streaming, sourceCount, strip, label, value };
    return value;
  }

  protected get time(): string {
    try {
      return new Intl.DateTimeFormat(this.locale, { hour: '2-digit', minute: '2-digit' }).format(
        this.message.timestamp,
      );
    } catch {
      return '';
    }
  }

  protected get isoTime(): string | null {
    const date = this.message?.timestamp;
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : null;
  }

  protected onPointerOver(event: Event): void {
    if (!this.preview) return;
    const target = eventTarget(event);
    if (!target) return;
    if (this.preview.isPopover(target)) {
      this.preview.cancelHide();
      return;
    }
    const marker = target.closest?.('.rag-citation') as HTMLElement | null;
    if (marker) this.preview.show(marker, this.sources);
  }

  protected onPointerOut(event: Event): void {
    if (!this.preview?.hasActiveMarker()) return;
    const target = eventTarget(event);
    const related = relatedTarget(event);
    const leaving = this.preview.isActiveMarker(target) || this.preview.isPopover(target);
    if (!leaving) return;
    if (this.preview.isActiveMarker(related) || this.preview.isPopover(related)) return;
    this.preview.scheduleHide();
  }

  protected onClick(event: MouseEvent): void {
    const marker = this.downloadMarker;
    if (!marker) return;
    const anchor = eventTarget(event)?.closest?.('a') as HTMLAnchorElement | null;
    const href = anchor?.getAttribute('href');
    if (!anchor || !href || !href.includes(marker)) return;
    event.preventDefault();
    this.download.emit({ url: anchor.href, filename: directText(anchor) });
  }

  protected async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.message.content);
    } catch {
      return;
    }
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 1600);
  }
}

function eventTarget(event: Event): HTMLElement | null {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const node = (path[0] ?? event.target) as Node | null;
  return node instanceof HTMLElement ? node : null;
}

function relatedTarget(event: Event): HTMLElement | null {
  const related = (event as MouseEvent | FocusEvent).relatedTarget;
  return related instanceof HTMLElement ? related : null;
}

function directText(anchor: HTMLAnchorElement): string {
  let text = '';
  anchor.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? '';
  });
  return text.trim() || (anchor.getAttribute('href') ?? '').split('/').filter(Boolean).pop() || 'document';
}
